import {
  Check,
  ChevronDown,
  ChevronUp,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import { Truncate } from "@lazarentertainment/web-design-system/components/truncate"
import * as Select from "@radix-ui/react-select"
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react"

export const SelectRoot = Select.Root
export const SelectPortal = Select.Portal

export type SelectItemProps = ComponentPropsWithoutRef<typeof Select.Item>

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Select.Item
        className={`select-item ${className ?? ""}`}
        {...props}
        ref={ref}
      >
        <Select.ItemText>{children}</Select.ItemText>
        <Select.ItemIndicator className="select-item-indicator">
          <Check />
        </Select.ItemIndicator>
      </Select.Item>
    )
  },
)

SelectItem.displayName = "SelectItem"

export type SelectTriggerProps = ComponentPropsWithoutRef<
  typeof Select.Trigger
> & {
  label: ReactNode
  placeholder?: ReactNode
}

export const SelectTrigger = forwardRef<
  HTMLButtonElement,
  SelectTriggerProps
>(({ label, placeholder = "Select", className, ...props }, ref) => {
  return (
    <Select.Trigger
      className={`select-trigger input-container ${className ?? ""}`}
      ref={ref}
      {...props}
    >
      <label htmlFor={props.id} className="input-label title-sm">
        {label}
      </label>
      <Truncate className="body-lg" data-line-count={1}>
        <Select.Value placeholder={placeholder} />
      </Truncate>
      <Select.Icon className="select-icon">
        <ChevronDown data-size="sm" />
      </Select.Icon>
    </Select.Trigger>
  )
})

SelectTrigger.displayName = "SelectTrigger"

export type SelectContentProps = ComponentPropsWithoutRef<
  typeof Select.Content
>

export const SelectContent = forwardRef<
  HTMLDivElement,
  SelectContentProps
>(({ children, className, ...props }, ref) => {
  return (
    <Select.Content
      className={`select-content ${className ?? ""}`}
      ref={ref}
      {...props}
      position="popper"
      sideOffset={4}
    >
      <Select.ScrollUpButton className="select-scroll-button">
        <ChevronUp data-size="xs" />
      </Select.ScrollUpButton>
      <Select.Viewport className="select-viewport">
        {children}
      </Select.Viewport>
      <Select.ScrollDownButton className="select-scroll-button">
        <ChevronDown data-size="xs" />
      </Select.ScrollDownButton>
    </Select.Content>
  )
})

SelectContent.displayName = "SelectContent"

export type SelectGroupProps = ComponentPropsWithoutRef<
  typeof Select.Group
>

export const SelectGroup = forwardRef<HTMLDivElement, SelectGroupProps>(
  ({ className, ...props }, ref) => {
    return (
      <Select.Group
        className={`select-group ${className ?? ""}`}
        ref={ref}
        {...props}
      />
    )
  },
)

SelectGroup.displayName = "SelectGroup"

export type SelectSeparatorProps = ComponentPropsWithoutRef<
  typeof Select.Separator
>

export const SelectSeparator = forwardRef<
  HTMLDivElement,
  SelectSeparatorProps
>(({ className, ...props }, ref) => {
  return (
    <Select.Separator
      className={`select-separator ${className ?? ""}`}
      ref={ref}
      {...props}
    />
  )
})

SelectSeparator.displayName = "SelectSeparator"
